import { memo, useCallback, useState } from 'react'

import { type UseSiteLinkSelection } from '@/features/analytics/hooks'
import { AnalyticsTableRowTypeSites } from '@/features/analytics/types'
import { useSavedErid, type SiteLinkMutate } from '@/shared/api'

import { Checkbox, RubleIconInline } from '@/shared/ui'

import { UTMLinkEditDialog } from '../UTMLinkEditDialog'
import {
    AverageReachCell,
    BudgetCell,
    CpmCell,
    CreationDateCell,
    CreativeCell,
    DateWithTimeCell,
    ErrCell,
    LinkCell,
    MarkingCell,
    NameCell,
    ReachCell,
    StatusCell,
    SubscribersCell,
    type BudgetCellProps,
    type MarkingCellProps,
    type NameCellProps,
    type StatusCellProps,
} from '../shared/cells'
import {
    useEditSingleFieldBounded,
    useMarkingFormQuery,
    useTelegramMarkingDataForShare,
    type UseEditSingleField,
} from '../shared/hooks'

import { AdvertiserAccount, Site } from '@/true-fsd/data/advertiser-account'
import { workspaceMethods } from '@synapex/shared2/entities'
import { parseTelegramPeerReference } from '@synapex/shared2/telegram'
import { useLinkToLink } from '../shared/hooks/useLinkToLink'
import {
    isColumnOptional,
    TABLE_COLUMNS_LAYOUT,
    type TableColumnOptionalKey,
    type TableReactNodeRecord,
} from './tableColumnsLayout'

const useOnChangeSiteLink = (mutate: SiteLinkMutate, siteLinkId: number) => {
    const onChangeStatus: StatusCellProps['onChange'] = useCallback(
        (newStatus) =>
            mutate({
                siteLinkId,
                siteLinkStatus: newStatus,
            }),
        [mutate, siteLinkId]
    )

    const onChangeName: NameCellProps['onChange'] = useCallback(
        (newName) =>
            mutate({
                siteLinkId,
                name: newName,
            }),
        [mutate, siteLinkId]
    )

    const onChangeBudget: BudgetCellProps['onChange'] = useCallback(
        (newBudget) =>
            mutate({
                siteLinkId,
                budget: newBudget,
            }),
        [mutate, siteLinkId]
    )

    const onChangeAdminContactId = useCallback(
        (editingRowId: number, selectedAdminContactId: number | null) =>
            mutate({
                siteLinkId: editingRowId,
                selectedAdminContactId,
            }),
        [mutate]
    )

    return {
        onChangeStatus,
        onChangeName,
        onChangeBudget,
        onChangeAdminContactId,
    }
}

export type TableRowProps = {
    skipColumns: Record<TableColumnOptionalKey, boolean>

    data: AnalyticsTableRowTypeSites
    selected: boolean
    onSelect: UseSiteLinkSelection['onLinkSelect']

    mutate: SiteLinkMutate
    groupId: number
    activeSite: Site
    activeAccount: AdvertiserAccount | null
} & UseEditSingleField &
    Pick<MarkingCellProps, 'markingStatus'>
export const TableRow = memo(function TableRow({
    skipColumns,

    data,
    selected,
    onSelect: onSelectProp,

    mutate,
    groupId,
    activeSite,

    activeAccount,

    editSingleField,
    editSingleFieldDispatch,

    markingStatus,
    ...nameTdProps
}: TableRowProps) {
    const {
        link_id,
        status,
        budget,
        creationDate,
        publishedAt,
        link,
        activeCreative,
        err,
        subscribers,
        averageReach,
        CPMForcasted,
        publishingDeletionDate,
        reach,
        CPM,
        users,
        CPU,
        CTRInPercent,
        leads,
        CPL,
        CInPercent,
        utmLink,
        publisher,
    } = data

    const wwld = {
        ...workspaceMethods.getWd(activeSite),
        link_id,
    }

    let editName = false
    let editBudget = false

    if (editSingleField && editSingleField.linkId === link_id) {
        editName = editSingleField.field === 'name'
        editBudget = editSingleField.field === 'budget'
    }

    const { maybeCopyLinkToLinkToClipboard, trElementId } = useLinkToLink({
        activeWorkspace: activeSite,
        groupId,
        link_id,
    })

    const onSelect = useCallback(() => {
        onSelectProp(data)
        maybeCopyLinkToLinkToClipboard()
    }, [onSelectProp, data, maybeCopyLinkToLinkToClipboard])

    const [budgetErrorKey, setBudgetErrorKey] = useState(0)
    const onBudgetError = useCallback(() => setBudgetErrorKey((k) => k + 1), [])

    const {
        onChangeBudget,
        onChangeName,
        onChangeStatus,
        onChangeAdminContactId,
    } = useOnChangeSiteLink(mutate, link_id)
    const { onChangeEditBudget, onChangeEditName } = useEditSingleFieldBounded(
        editSingleFieldDispatch,
        link_id
    )

    const { ref, query: markingModalFormDataQuery } = useMarkingFormQuery(
        wwld,
        Boolean(
            markingStatus.status === 'available' &&
                parseTelegramPeerReference(data.name)
        )
    )

    const ordCreativeFlag = markingModalFormDataQuery.data?.creative_flag
    const allowShareCreative =
        ordCreativeFlag &&
        (markingModalFormDataQuery.data?.mark_text ||
            markingModalFormDataQuery.data?.mark_media)

    const publishOptionFlag =
        markingModalFormDataQuery.data?.publish_option_flag

    const savedEridKktu = useSavedErid({
        ...wwld,
        draftContractId: markingModalFormDataQuery.data?.draft_contract_id,
        agencyContractId: markingModalFormDataQuery.data?.agency_contract_id,
        creativeId: activeCreative?.id,
        customContractId: markingModalFormDataQuery.data?.custom_contract_id,
    })

    const telegramMarkingData = useTelegramMarkingDataForShare({
        ...wwld,
        markingFormData: markingModalFormDataQuery.data ?? null,
        savedErid: savedEridKktu.savedErid ?? null,
        adOrdContragentId:
            markingStatus.status === 'available'
                ? (markingStatus.adOrdContragentId ?? null)
                : null,
        markingStatusAvailable: markingStatus.status === 'available',
    })

    const hasMarkingFormData = Boolean(markingModalFormDataQuery.data)

    const tableRow: TableReactNodeRecord = {
        marking: skipColumns.marking ? null : (
            <MarkingCell
                {...{
                    ...wwld,
                    savedEridKktu,
                    activeAccount,
                    data,
                    onChangeAdminContactId,
                    markingStatus,
                    groupId,
                    markingModalFormDataQuery,
                    budget,
                    onBudgetError,
                }}
            />
        ),
        select: (
            <div className="flex items-center justify-center">
                <Checkbox checked={selected} onChange={onSelect} />
            </div>
        ),
        creationDate: <CreationDateCell>{creationDate}</CreationDateCell>,
        name: (
            <NameCell
                {...nameTdProps}
                onChange={onChangeName}
                data={data}
                edit={editName}
                onChangeEdit={onChangeEditName}
            />
        ),
        status: (
            <StatusCell
                value={status}
                onChange={onChangeStatus}
                className={'w-full'}
            />
        ),
        creative: (
            <CreativeCell
                {...wwld}
                withShare
                activeCreative={activeCreative}
                published={status === 'published'}
                privateChannel={
                    publisher && !publisher.telegram_channel_username
                }
                postDeleted={Boolean(publisher?.link_deleted_at)}
                groupId={groupId}
                disabled={ordCreativeFlag}
                allowShare={allowShareCreative}
                marking={telegramMarkingData}
                markableFile={activeCreative?.markableFile}
            />
        ),
        link: (
            <LinkCell link={link}>
                <UTMLinkEditDialog
                    disabled={status === 'published'}
                    groupId={groupId}
                    utmLink={utmLink}
                    activeSite={activeSite}
                    siteLink={data.source}
                    erid={savedEridKktu.savedErid ?? undefined}
                />
            </LinkCell>
        ),
        budget: (
            <BudgetCell
                budget={budget}
                onChange={onChangeBudget}
                edit={editBudget}
                onChangeEdit={onChangeEditBudget}
                enabled={!publishOptionFlag}
                disabledMessage="Чтобы изменить бюджет, уберите галочку 'Размещение забронировано' в форме маркировки"
                errorShakeKey={budgetErrorKey}
                hasMarkingFormData={hasMarkingFormData}
            />
        ),
        subscribers: (
            <SubscribersCell linkId={link_id} subscribers={subscribers} />
        ),
        averageReach: <AverageReachCell>{averageReach}</AverageReachCell>,
        err: <ErrCell>{err}</ErrCell>,
        CPMForcasted: <CpmCell>{CPMForcasted}</CpmCell>,
        publishedAt: <DateWithTimeCell>{publishedAt}</DateWithTimeCell>,
        publishingDeletionDate: (
            <DateWithTimeCell>{publishingDeletionDate}</DateWithTimeCell>
        ),
        reach: <ReachCell>{reach}</ReachCell>,

        CPM: <CpmCell>{CPM}</CpmCell>,

        users,
        CPU: (
            <>
                {CPU.toFixed(0)} <RubleIconInline />
            </>
        ),
        CTRInPercent: <>{CTRInPercent.toFixed(2)}%</>,
        leads: <>{leads.toFixed(0)}</>,
        CPL: (
            <>
                {CPL.toFixed(0)}
                <RubleIconInline />
            </>
        ),
        CInPercent: <>{CInPercent.toFixed(2)}%</>,
    }

    return (
        <tr ref={ref} id={trElementId}>
            {TABLE_COLUMNS_LAYOUT.filter(
                (column) =>
                    !(isColumnOptional(column.key) && skipColumns[column.key])
            ).map(({ key, style, className }) => (
                <td key={key} style={style} className={className}>
                    {tableRow[key]}
                </td>
            ))}
        </tr>
    )
})
